// AuthContext — React Context wrapping Firebase Auth state.
// -----------------------------------------------------------------
// Per docs/architecture/m3-spa-scaffold-plan.md § 2.5 + dispatch
// m3-2-auth-rules-listener.md § 6.5.
//
// Surfaces:
//   - `user`: Firebase User | null
//   - `claims`: { admin?: boolean; qa?: boolean } | null
//   - `loading`: true while Auth is hydrating from IndexedDB
//   - `signInWithGoogle()`, `signInWithEmail(email, pw)`,
//     `signOutUser()`
//
// Uses `onIdTokenChanged` (NOT `onAuthStateChanged`):
// `onIdTokenChanged` fires on token refresh too, so a claims
// change (granted via scripts/auth/grant_admin.py) flows into the
// context as soon as the SDK refreshes the token (within ~1h, or
// immediately on `getIdToken(true)`). This makes "lead grants
// admin claim → user re-signs-in" the canonical flow without a
// claim-management UI (per dispatch § 3.8).

import {
  GoogleAuthProvider,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { auth } from '@/lib/firebase';

export type AuthClaims = {
  admin?: boolean;
  qa?: boolean;
};

export type AuthState = {
  user: User | null;
  claims: AuthClaims | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signOutUser: () => Promise<void>;
};

// Exported as a named export so component tests can mock the auth
// state via `<AuthContext.Provider value={mockAuth}>` (the canonical
// pattern for any future component test that consumes useAuth — see
// docs/architecture/dispatches/m-test-3-component-patterns-coverage.md
// § 3.4 + the AppShell test under tests/components/). Logged as
// M-Test-F6: the M-Test.3 dispatch assumed M3.2 had already shipped
// this export; it had not, so the keyword landed here at M-Test.3
// commit 1 as a 1-line prerequisite.
export const AuthContext = createContext<AuthState | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<AuthClaims | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // onIdTokenChanged fires on:
    //   1. initial auth-state hydration (resolves `loading`)
    //   2. sign-in / sign-out
    //   3. token refresh (~1h cadence; carries new custom claims)
    const unsubscribe = onIdTokenChanged(auth, async (nextUser) => {
      setUser(nextUser);
      if (nextUser) {
        // Claim shape: { admin: boolean; qa: boolean }. Read from
        // the freshly-refreshed token. No `forceRefresh: true` —
        // the listener already fires on refresh, so we read what
        // the SDK has cached and avoid an extra round-trip.
        const tokenResult = await nextUser.getIdTokenResult();
        const tokenClaims = tokenResult.claims as AuthClaims;
        setClaims({
          admin: tokenClaims.admin === true,
          qa: tokenClaims.qa === true,
        });
      } else {
        setClaims(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    // Default scopes (email + profile) are exactly what we need;
    // the rule predicate reads `request.auth.token.email` which
    // requires the email scope to be present.
    await signInWithPopup(auth, provider);
  }, []);

  const signInWithEmail = useCallback(
    async (email: string, password: string) => {
      await signInWithEmailAndPassword(auth, email, password);
    },
    [],
  );

  const signOutUser = useCallback(async () => {
    await signOut(auth);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      claims,
      loading,
      signInWithGoogle,
      signInWithEmail,
      signOutUser,
    }),
    [user, claims, loading, signInWithGoogle, signInWithEmail, signOutUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthState => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an <AuthProvider>');
  }
  return ctx;
};
